import { auth } from '../firebase';

const DEVICE_ID_KEY = 'valenpos.offlineDeviceId.v1';
const DEVICE_AUTHORIZATION_KEY = 'valenpos.offlineDeviceAuthorization.v1';
export const OFFLINE_DEVICE_CHANGE_EVENT = 'valenpos:offline-device-change';

export class OfflineDeviceError extends Error {
  constructor(message, code = 'OFFLINE_DEVICE_FAILED', status = 0, data = null) {
    super(message);
    this.name = 'OfflineDeviceError';
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

const randomId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

export function getOfflineDeviceId() {
  if (typeof window === 'undefined') return '';
  let deviceId = window.localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = `pc-${randomId()}`;
    window.localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

export function getCachedOfflineDeviceAuthorization() {
  if (typeof window === 'undefined') return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(DEVICE_AUTHORIZATION_KEY) || 'null');
    if (!value?.active || value.deviceId !== getOfflineDeviceId()) return null;
    return value;
  } catch (_error) {
    return null;
  }
}

function cacheAuthorization(value) {
  if (typeof window === 'undefined') return;
  if (value?.active) {
    window.localStorage.setItem(DEVICE_AUTHORIZATION_KEY, JSON.stringify({
      ...value,
      deviceId: getOfflineDeviceId(),
      cachedAt: Date.now(),
    }));
  } else {
    window.localStorage.removeItem(DEVICE_AUTHORIZATION_KEY);
  }
  window.dispatchEvent(new CustomEvent(OFFLINE_DEVICE_CHANGE_EVENT));
}

async function callDeviceApi(payload) {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new OfflineDeviceError('Debes iniciar sesión para configurar este equipo.', 'AUTH_REQUIRED');
  let response;
  try {
    response = await fetch('/api/cash/open', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...payload,
        action: `offlineDevice${String(payload.action || 'status').replace(/^./, (letter) => letter.toUpperCase())}`,
        deviceId: getOfflineDeviceId(),
      }),
    });
  } catch (_error) {
    throw new OfflineDeviceError('No se pudo verificar este equipo porque no hay conexión.', 'OFFLINE_DEVICE_NETWORK');
  }
  const text = await response.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch (_error) { data = {}; }
  if (!response.ok) {
    throw new OfflineDeviceError(
      data?.error || 'No se pudo configurar este equipo.',
      data?.code || 'OFFLINE_DEVICE_FAILED',
      response.status,
      data
    );
  }
  cacheAuthorization(data?.device || null);
  return data?.device || null;
}

export const refreshOfflineDeviceAuthorization = () => callDeviceApi({ action: 'status' });
export const authorizeOfflineDevice = ({ label = '', replace = false } = {}) => (
  callDeviceApi({ action: 'register', label, replace })
);
export const disableOfflineDevice = () => callDeviceApi({ action: 'disable' });

export function canOpenCashOffline(currentUser, storeId = 'SUC001') {
  const authorization = getCachedOfflineDeviceAuthorization();
  const role = String(currentUser?.role || '').trim().toLowerCase();
  const allowedRole = ['cajero', 'admin', 'administrador', 'ti'].includes(role);
  return Boolean(
    allowedRole
    && authorization?.active
    && String(authorization.storeId || 'SUC001') === String(storeId || 'SUC001')
  );
}
